import apiFetch from '@/utils/apiFetch';
import { defineStore } from 'pinia';
import DefaultConfig from '@/config/default';
import type { Service, Subscription } from '@/config/types/responses';

// eslint-disable-next-line import/prefer-default-export
export const useServicesStore = defineStore('services', {
  state: () => ({
    loading: false,
    error: null as string | null,
    success: null as string | null,
    services: [] as Service[],
    subscriptions: [] as Subscription[],
  }),

  actions: {
    async fetchServices() {
      this.error = null;
      this.loading = true;
      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/admin/services`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) throw new Error('Error fetching services');
        const data = await res.json();
        this.services = data.data?.results ?? data.data ?? [];
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async fetchSubscriptions(organizationId: string) {
      this.error = null;
      this.loading = true;
      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/services/public/subscriptions/${organizationId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) throw new Error('Error fetching subscriptions');
        const data = await res.json();
        this.subscriptions = data.data?.results ?? data.data ?? [];
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async fetchOrgSubscriptions(organizationId: string) {
      this.error = null;
      this.loading = true;
      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/admin/subscriptions/organization/${organizationId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) throw new Error('Error fetching subscriptions');
        const data = await res.json();
        this.subscriptions = data.data?.results ?? data.data ?? [];
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async createSubscription(organizationId: string, serviceId: string, rate?: number | string) {
      this.error = null;
      this.success = null;
      this.loading = true;
      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const payload: Record<string, any> = { organizationId, serviceId };
        if (rate !== undefined && rate !== '') {
          payload.rate = typeof rate === 'string' ? parseFloat(rate) : rate;
        }
        const res = await apiFetch(`${defaultConfig.baseURL}/api/admin/subscriptions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) {
          this.error = data.data?.error || data.error || 'Error creating subscription';
          return false;
        }
        this.success = 'Subscription created successfully';
        return true;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
        return false;
      } finally {
        this.loading = false;
      }
    },
  },
});
